import React, { useEffect, useRef } from "react";

/**
 * The sections the drawer can reach.
 *
 * `ready: false` is listed but not openable, so the shape of the app is
 * visible. `hidden: true` is not listed at all — it is finished work that is
 * not being shown to visitors yet, which is a different thing from unfinished
 * work and deserves a different flag. Hidden sections still route and still
 * build; unlocking dev tools reveals them, so they can be demonstrated without
 * a deploy.
 */
export const SECTIONS = [
  { id: "stack",      label: "Price a stack", hint: "Paste a pile, get a number",    ready: true },
  /* Reached from a search result now rather than from here. It was a screen
     that asked you to type a name and a number you had just typed into Search
     — and on Pokemon that retyping spent a second billed lookup to arrive at a
     card the app was already holding. Hidden rather than deleted: it still
     routes, which is how Search gets to it, and it is still the screen that
     answers "what does this fetch in a slab". */
  { id: "single",     label: "Single card",  hint: "One card, with graded comps",    ready: true, hidden: true },
  { id: "search",     label: "Search",       hint: "Find cards and add them",        ready: true },
  /* One entry, because a person thinks "where are my cards", not "is this an
     ownership record or an intent record". The collection is the first chip
     inside; the split it used to have in the nav is still there underneath. */
  { id: "cards",      label: "Collection",   hint: "What you own, and what you're after",   ready: true },
  /* A chip on the Collection screen now, beside the lists and the total it
     reads from. This entry said "what you're chasing" directly under one that
     said "what you're after" — one sentence, two tabs. Hidden rather than
     deleted, so the route still works. */
  { id: "goals",      label: "Goals",        hint: "What you're chasing, and what you're not", ready: true, hidden: true },
  /* Called Grading because that is the question being asked — is this worth
     sending off. It answers it on centering alone, which the screen says
     plainly and more than once, because the name promises more than it does. */
  { id: "grading",    label: "Grading",      hint: "Centering, and what it allows",   ready: true, hidden: true },
  { id: "binder",     label: "Binder",       hint: "Lay a page out before you sleeve it", ready: true, hidden: true },
  { id: "submissions", label: "Submissions", hint: "Cards you sent to grade, and what they made", ready: true },
  // No One Piece entry: it was a section, which made every other screen
  // silently Pokemon-only. It's a mode now, switched in the top bar.
  { id: "sell",       label: "Want to sell", hint: "Later",                          ready: false },
];

export default function NavDrawer({ open, onClose, active, onNavigate, onBackup, devUnlocked = false, signedIn = false }) {
  const panelRef = useRef(null);
  const firstItemRef = useRef(null);
  const shown = SECTIONS.filter((s) => devUnlocked || !s.hidden);

  // Escape closes the drawer from anywhere while it's open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Move focus into the drawer when it opens so keyboard users land inside it.
  useEffect(() => {
    if (open && firstItemRef.current) firstItemRef.current.focus();
  }, [open]);

  return (
    <>
      <div
        className={open ? "nav-scrim nav-scrim--open" : "nav-scrim"}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        id="nav-drawer"
        ref={panelRef}
        className={open ? "nav-drawer nav-drawer--open" : "nav-drawer"}
        aria-label="Sections"
        aria-hidden={open ? undefined : true}
      >
        <p className="nav-drawer-heading">Go to</p>
        <nav>
          <ul className="nav-list">
            {shown.map((s, i) => {
              const isActive = s.id === active;
              return (
                <li key={s.id}>
                  <button
                    ref={i === 0 ? firstItemRef : undefined}
                    className={
                      "nav-item" +
                      (isActive ? " nav-item--active" : "") +
                      (s.ready ? "" : " nav-item--pending")
                    }
                    onClick={() => { if (s.ready) { onNavigate(s.id); onClose(); } }}
                    disabled={!s.ready}
                    aria-current={isActive ? "page" : undefined}
                    tabIndex={open ? 0 : -1}
                  >
                    <span className="nav-item-label">{s.label}</span>
                    <span className="nav-item-hint">{s.hint}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Down here rather than as a section of its own. It is about the whole
            app rather than a place in it, and the app has enough sections. */}
        {onBackup && (
          <button
            type="button"
            className="nav-backup"
            onClick={() => { onBackup(); onClose(); }}
            tabIndex={open ? 0 : -1}
          >
            {/* The same button either way; only the sentence changes. "It
                only lives in this browser" is the reason to press it when
                signed out and a false statement when signed in — and a false
                sentence about where somebody's data lives is worse than a
                redundant button. Signed in, the file is the one copy that
                does not depend on the account, the sync, or us being right. */}
            <span className="nav-item-label">
              {signedIn ? "Export everything" : "Back up everything"}
            </span>
            <span className="nav-item-hint">
              {signedIn ? "A copy of your account, as a file you keep" : "It only lives in this browser"}
            </span>
          </button>
        )}
      </aside>
    </>
  );
}
